import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

export default function StoriesPage() {
  const { t } = useTranslation();
  const [storyGroups, setStoryGroups] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [storyIdx, setStoryIdx] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stories/entries");
        setStoryGroups(data.story_groups || []);
      } catch {
        setStoryGroups([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openStory = (group) => {
    setViewing(group);
    setStoryIdx(0);
  };

  const closeViewer = () => {
    setViewing(null);
    setStoryIdx(0);
  };

  const nextStory = () => {
    if (!viewing) return;
    if (storyIdx < viewing.stories.length - 1) {
      setStoryIdx(storyIdx + 1);
    } else {
      closeViewer();
    }
  };

  // Story Viewer
  if (viewing) {
    const story = viewing.stories[storyIdx];
    return (
      <div data-testid="story-viewer" className="fixed inset-0 z-[100] bg-black flex flex-col" onClick={nextStory}>
        {/* Progress */}
        <div className="flex gap-1 px-2 pt-2 pb-1">
          {viewing.stories.map((_, i) => (
            <div key={i} className="flex-1 h-0.5 bg-white/30 overflow-hidden">
              <div className={`h-full transition-all duration-[5000ms] ${i < storyIdx ? "w-full bg-white" : i === storyIdx ? "w-full bg-white animate-progress" : "w-0"}`} />
            </div>
          ))}
        </div>
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">
            {(viewing.author?.display_name || "?")[0]}
          </div>
          <span className="text-white text-sm font-medium">{viewing.author?.display_name}</span>
          <button
            data-testid="story-close"
            onClick={(e) => { e.stopPropagation(); closeViewer(); }}
            className="ml-auto text-white/70 hover:text-white text-xl"
          >
            &times;
          </button>
        </div>
        {/* Media */}
        <div className="flex-1 flex items-center justify-center px-4 relative">
          <img src={story.media_url} alt="" className="max-w-full max-h-full object-contain" />
          {story.caption && (
            <div className="absolute bottom-8 left-4 right-4 bg-gradient-to-t from-black/60 to-transparent p-4">
              <p className="text-white text-sm">{story.caption}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
  }

  return (
    <div data-testid="stories-page" className="px-4 py-4">
      <h1 className="text-lg font-bold text-[#0A0A0A] mb-4">Stories</h1>
      {storyGroups.length === 0 ? (
        <p className="text-sm text-[#999] text-center py-8">{t("stories_extra.empty")}</p>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {storyGroups.map((group) => (
            <button
              key={group.author_id}
              data-testid={`stories-group-${group.author_id}`}
              onClick={() => openStory(group)}
              className="relative aspect-[3/4] bg-[#F0F0F0] overflow-hidden group active:scale-[0.98] transition-transform"
            >
              {group.stories[0]?.media_url && (
                <img src={group.stories[0].media_url} alt="" className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <p className="text-white text-xs font-medium">{group.author?.display_name}</p>
                <p className="text-white/60 text-[10px]">{group.stories.length} Story{group.stories.length > 1 ? "s" : ""}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
